import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'

import { splitDestination } from './navActive'
import { NAVIGATION, PLATFORM_NAVIGATION } from './navigation'

import { Avatar, Dialog, IconButton } from '@/components/primitives'
import { Icon } from '@/components/primitives/Icon'
import { useSession, useSignOut } from '@/lib/auth/session'

function readTheme(): boolean {
  try {
    const saved = localStorage.getItem('convo:theme')
    return saved
      ? saved === 'dark'
      : window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}
export function Topbar({
  onToggleSidebar,
  onOpenSearch,
}: {
  onToggleSidebar: () => void
  onOpenSearch: () => void
}) {
  const { user } = useSession()
  const signOut = useSignOut()
  const { workspaceSlug } = useParams()
  const { pathname, search } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [dark, setDark] = useState(readTheme)
  const [signOutError, setSignOutError] = useState('')
  const name =
    (user?.user_metadata?.['full_name'] as string | undefined) ??
    user?.email ??
    'Your account'
  const platform = !workspaceSlug
  const sections = platform ? PLATFORM_NAVIGATION : NAVIGATION
  const route = platform ? pathname : pathname.replace(`/w/${workspaceSlug}`, '')
  const params = new URLSearchParams(search)

  // The deepest match wins, so /team/departments reads "Departments" rather
  // than "Team", and ?tab=invoices reads "Invoices" rather than "Billing".
  const item = sections
    .flatMap((s) => s.items)
    .filter((i) => i.path === route || (i.path && route.startsWith(`${i.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0]

  const child = item?.children?.find((c) => {
    const { path, params: want } = splitDestination(c.to)
    if (path !== route) return false
    return [...want].every(([key, value]) => params.get(key) === value)
  })

  const page = child?.label ?? item?.label ?? (platform ? 'Platform' : 'Workspace')
  const trail = child && item ? item.label : platform ? 'Platform' : 'Workspace'
  useEffect(() => {
    document.documentElement.dataset['theme'] = dark ? 'dark' : 'light'
    try {
      localStorage.setItem('convo:theme', dark ? 'dark' : 'light')
    } catch {
      /* Preference only. */
    }
  }, [dark])
  return (
    <header className="workspace-topbar">
      <IconButton
        variant="ghost"
        icon={<Icon name="menu" />}
        label="Toggle navigation"
        onClick={onToggleSidebar}
      />
      <div className="topbar-breadcrumb">
        <span className="text-text-tertiary">{trail}</span>
        <Icon name="chevron" size={13} />
        <strong>{page}</strong>
      </div>
      {workspaceSlug && (
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label="Search pages"
          className="topbar-search"
        >
          <Icon name="search" size={17} />
          <span>Search pages…</span>
          <kbd>⌘ K</kbd>
        </button>
      )}
      <div className="topbar-actions">
        <IconButton
          variant="ghost"
          icon={<Icon name={dark ? 'sun' : 'moon'} />}
          label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={() => setDark((v) => !v)}
        />
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Account menu"
          className="account-trigger"
        >
          <Avatar name={name} size="sm" presence="online" />
        </button>
      </div>
      <Dialog
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title="Your account"
        size="sm"
      >
        <div className="flex items-center gap-3">
          <Avatar name={name} />
          <div className="min-w-0">
            <p className="truncate font-semibold">{name}</p>
            <p className="truncate text-compact text-text-secondary">
              {user?.email}
            </p>
          </div>
        </div>
        {signOutError && (
          <p role="alert" className="mt-4 text-danger">
            {signOutError}
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            void signOut().catch(() =>
              setSignOutError('Could not sign out. Please try again.'),
            )
          }}
          className="mt-5 flex min-h-[44px] w-full items-center gap-3 rounded-control bg-danger-subtle px-4 text-compact text-danger"
        >
          <Icon name="logout" size={18} />
          Sign out
        </button>
      </Dialog>
    </header>
  )
}
